<script lang="ts">
import type { StudyChargeIqrVizLayout } from '$lib/components/study/slide-layout-types'

/**
 * `.cc` card: charge per claim IQR band, median bar, mean dashed line, axis labels, legend.
 * `subtitleHtml` and `legendHtml` are trusted `{@html}`.
 */
interface Props {
  title: string
  titleStyle?: string
  subtitleHtml: string
  subtitleStyle?: string
  viz: StudyChargeIqrVizLayout
  formatSpend: (n: number) => string
  legendHtml?: string
  cardClass?: string
  cardStyle?: string
}

let {
  title,
  titleStyle = 'font-size:0.92rem;font-weight:700;letter-spacing:-0.01em;',
  subtitleHtml,
  subtitleStyle = 'font-size:0.8rem;line-height:1.45;color:var(--t2);margin-bottom:0.65rem;',
  viz,
  formatSpend,
  legendHtml = '<strong style="color:var(--g);">Teal band</strong> &mdash; interquartile range (middle 50%). <strong style="color:var(--g);">Solid bar</strong> &mdash; median. <strong style="color:#ffd166;">Dashed line</strong> &mdash; mean (average) charge.',
  cardClass = '',
  cardStyle,
}: Props = $props()
</script>

<div class="cc {cardClass}" style={cardStyle}>
  <div class="ct" style={titleStyle}>{title}</div>
  <div class="cs" style={subtitleStyle}>{@html subtitleHtml}</div>
  <div style="padding:.95rem .15rem .35rem;">
    <div
      style="display:flex;justify-content:space-between;font-size:0.82rem;color:var(--t2);font-family:'DM Mono',monospace;font-weight:500;margin-bottom:.55rem;"
    >
      <span>$0</span>
      <span>{formatSpend(viz.rangeMax)}</span>
    </div>
    <div style="position:relative;height:60px;">
      <div
        style="position:absolute;left:0;right:0;top:50%;height:5px;margin-top:-2.5px;background:rgba(255,255,255,.1);border-radius:2px;"
      ></div>
      <div
        title="P25–P75: middle 50% of claims by charge"
        style="position:absolute;top:20px;left:{viz.p25Pct}%;width:{viz.iqrW}%;height:22px;background:rgba(17,160,165,.38);border:1px solid rgba(17,160,165,.55);border-radius:7px;box-sizing:border-box;"
      ></div>
      <div
        title="Median charge per claim"
        style="position:absolute;top:9px;left:{viz.medPct}%;width:4px;height:42px;margin-left:-2px;background:var(--g);border-radius:2px;box-shadow:0 0 0 2px rgba(3,11,10,.35);"
      ></div>
      <div
        title="Mean (average) charge per claim"
        style="position:absolute;top:11px;left:{viz.meanPct}%;height:38px;width:0;margin-left:-1px;border-left:3px dashed #ffd166;opacity:0.95;"
      ></div>
    </div>
    <div
      style="position:relative;min-height:3.1rem;margin-top:.65rem;padding:0 .1rem;line-height:1.35;"
    >
      <span
        style="position:absolute;left:{viz.p25Pct}%;transform:translateX(-50%);font-family:'DM Mono',monospace;font-size:0.8rem;text-align:center;color:var(--t2);max-width:min(28vw,7.5rem);white-space:normal;"
        >{formatSpend(viz.p25)}
        <span style="display:block;font-size:0.68rem;opacity:.85;font-weight:600;margin-top:.12rem;"
          >P25</span
        >
      </span>
      <span
        style="position:absolute;left:{viz.medPct}%;transform:translateX(-50%);text-align:center;color:var(--g);font-weight:700;font-family:'DM Serif Display',serif;font-size:0.88rem;max-width:min(28vw,7.5rem);white-space:normal;"
        >{formatSpend(viz.med)}
        <span
          style="display:block;font-size:0.68rem;font-family:system-ui,sans-serif;font-weight:600;opacity:.9;margin-top:.1rem;"
          >median</span
        >
      </span>
      <span
        style="position:absolute;left:{viz.meanPct}%;transform:translateX(-50%);text-align:center;color:#ffd166;font-weight:700;font-family:'DM Serif Display',serif;font-size:0.88rem;max-width:min(28vw,7.5rem);white-space:normal;"
        >{formatSpend(viz.mean)}
        <span
          style="display:block;font-size:0.68rem;font-family:system-ui,sans-serif;font-weight:600;opacity:.95;margin-top:.1rem;color:var(--t2);"
          >mean</span
        >
      </span>
      <span
        style="position:absolute;left:{viz.p75Pct}%;transform:translateX(-50%);font-family:'DM Mono',monospace;font-size:0.8rem;text-align:center;color:var(--t2);max-width:min(28vw,7.5rem);white-space:normal;"
        >{formatSpend(viz.p75)}
        <span style="display:block;font-size:0.68rem;opacity:.85;font-weight:600;margin-top:.12rem;"
          >P75</span
        >
      </span>
    </div>
    <p style="font-size:0.84rem;color:var(--t2);margin:.85rem 0 0;line-height:1.55;">
      {@html legendHtml}
    </p>
  </div>
</div>
